// Вход/регистрация покупателя по кадрам «Авторизация» 3948:50094 / 50870 /
// 50985 / 51093: карточка 366px #F8F8F8 r20, p24, элементы через 20; заголовок
// 20/23 w600. Шаг 1 — подпись 14/18 w500 #2E2E2E, через 8 поле телефона
// 318×48 r20 белое (16/21 w500, маска «+7 …»), согласие 14/18 w500 #1F1F1F 64 %
// (чекбокс 20 r5, отмеченный #FF5E00) и кнопка «Получить код» 318×48 r12, 16/21
// w600 (неактивная — #989AA2 12 %, активная — #2E2E2E). Шаг 2 — СМС-код:
// точки 8 через 32, цифры 26/30 w700 через 28 (зелёные #A9DBA9, когда код
// принят), таймер 14/18 #000 50 %. Ячеек столько, сколько цифр в коде бэкенда
// (6; в макете нарисовано 4).
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/text-field";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store";
import { OAuthButtons } from "./oauth-buttons";
const CODE_LENGTH = 6;
const RESEND_SECONDS = 59;
const TERMS_URL = "/docs/b2c/" + encodeURIComponent("Пользовательское соглашение OSMIX.pdf");
const PRIVACY_URL = "/docs/b2c/" + encodeURIComponent("Политика конфиденциальности OSMIX.pdf");

// Российский номер: храним только 10 цифр после +7, показываем «+7 915 563 96 36».
const digitsOf = (value: string) => {
  let d = value.replace(/\D/g, "");
  if (d.startsWith("7") || d.startsWith("8")) d = d.slice(1);
  return d.slice(0, 10);
};
const formatPhone = (d: string) => {
  const parts = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 8), d.slice(8, 10)].filter(Boolean);
  return `+7 ${parts.join(" ")}`.trimEnd();
};
// «+7 (900) 000 - 00 - 00» — как в подписи экрана кода.
const formatPhoneLong = (d: string) => `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)} - ${d.slice(6, 8)} - ${d.slice(8, 10)}`;

// compact — карточка внутри другого окна (панель помощника): без отступов
// страницы и на белом, потому что фон панели тот же #F8F8F8, что у карточки.
// oauth=false - без кнопок Яндекс ID / VK ID (экран «подтвердите номер» после
// входа через сервис, который не отдал телефон).
export function SignInCard({
  onSignedIn,
  compact = false,
  oauth = true
}: {
  onSignedIn: () => void;
  compact?: boolean;
  oauth?: boolean;
}) {
  const startPhoneAuth = useProfile(s => s.startPhoneAuth);
  const verifyPhoneAuth = useProfile(s_0 => s_0.verifyPhoneAuth);
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [code, setCode] = useState("");
  const [codeState, setCodeState] = useState<"idle" | "ok" | "error">("idle");
  const [left, setLeft] = useState(RESEND_SECONDS);
  const [busy, setBusy] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const canRequest = phone.length === 10 && consent && !busy;
  useEffect(() => {
    if (!sessionId || left <= 0) return;
    const id = window.setTimeout(() => setLeft(s_1 => s_1 - 1), 1000);
    return () => window.clearTimeout(id);
  }, [sessionId, left]);
  const start = async () => {
    if (!canRequest && !sessionId) return;
    setBusy(true);
    try {
      const started = await startPhoneAuth({
        phone: `+7${phone}`,
        deviceName: "Browser"
      });
      setSessionId(started.sessionId);
      setCode("");
      setCodeState("idle");
      setLeft(RESEND_SECONDS);
      window.setTimeout(() => codeRef.current?.focus(), 50);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось отправить код");
    } finally {
      setBusy(false);
    }
  };
  const verify = async (value: string) => {
    setBusy(true);
    try {
      const me = await verifyPhoneAuth({
        sessionId,
        code: value,
        deviceName: "Browser"
      });
      if (!me) {
        setCodeState("error");
        toast.error("Неверный код");
        return;
      }
      setCodeState("ok");
      onSignedIn();
    } catch (e_0) {
      setCodeState("error");
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось проверить код");
    } finally {
      setBusy(false);
    }
  };
  const onCodeChange = (raw: string) => {
    const next = raw.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(next);
    setCodeState("idle");
    if (next.length === CODE_LENGTH) void verify(next);
  };
  return (
    // Карточка входа в кадрах на 115 ниже шапки: 10 даёт <main>, 105 — здесь.
    <div className={cn("flex justify-center", !compact && "py-10 md:pt-[105px] md:pb-[88px]")}>
			{!sessionId ? <form className={cn("flex w-full max-w-[366px] flex-col gap-5 rounded-[20px] p-6", compact ? "bg-white" : "bg-[#F8F8F8]")} onSubmit={e_1 => {
        e_1.preventDefault();
        void start();
      }}>
					<h1 className="text-center text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-black">Добро пожаловать в OSMIX</h1>
					<label className="flex flex-col gap-2">
						<span className="text-[14px] leading-[18px] font-medium text-[#2E2E2E]">Для входа или регистрации в OSMIX введите номер телефона</span>
						<Input size="lg" data-testid="buyer-phone" type="tel" inputMode="tel" autoComplete="tel" value={formatPhone(phone)} onChange={e_2 => setPhone(digitsOf(e_2.target.value))} placeholder="+7" aria-label="Номер телефона" fieldClassName={compact ? undefined : "bg-white"} className="placeholder:text-ds-primary" />
					</label>
					<label className="flex cursor-pointer items-center gap-2.5">
						<input data-testid="buyer-consent" type="checkbox" checked={consent} onChange={e_3 => setConsent(e_3.target.checked)} className="peer sr-only" />
						<span aria-hidden="true" className={cn("grid size-5 shrink-0 place-items-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#1F1F1F]/40", consent ? "border-[#FF5E00] bg-[#FF5E00] text-white" : "border-[#1F1F1F]/24 bg-transparent")}>
							{consent && <Check className="size-3.5" strokeWidth={2.5} />}
						</span>
						<span className="text-[14px] leading-[18px] font-medium text-[#1F1F1F]/64">
							Я соглашаюсь с{" "}
							<a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#1F1F1F]">
								правилами пользования торговой площадкой
							</a>{" "}
							и{" "}
							<a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#1F1F1F]">
								политикой конфиденциальности.
							</a>
						</span>
					</label>
					{/* Активная кнопка в кадре #2E2E2E; неактивная — серая DS. */}
					<Button data-testid="buyer-auth-submit" type="submit" block disabled={!canRequest} className="bg-ds-ink-2 hover:bg-ds-primary">
						Получить код
					</Button>
					{oauth && <OAuthButtons consent={consent} onNeedConsent={() => setConsent(false)} />}
				</form> : <div className={cn("flex w-full max-w-[366px] flex-col items-center gap-5 rounded-[20px] p-6 text-center", compact ? "bg-white" : "bg-[#F8F8F8]")}>
					<h1 className="text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-black">Для регистрации в OSMIX введите СМС-код</h1>
					<p className="text-[14px] leading-[18px] font-medium text-[#2E2E2E]">
						Мы отправили код на номер
						<br />
						{formatPhoneLong(phone)}
					</p>
					<label className="relative flex h-[30px] cursor-text items-center justify-center gap-7">
						<input ref={codeRef} data-testid="buyer-sms-code" value={code} onChange={e_4 => onCodeChange(e_4.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={CODE_LENGTH} aria-label="Код из СМС" className="absolute inset-0 opacity-0" />
						{Array.from({
            length: CODE_LENGTH
          }, (_, i) => {
            const digit = code[i];
            return digit ? <span key={i} className={cn("w-3 text-center text-[26px] leading-[30px] font-bold tracking-[-0.31px]", codeState === "ok" ? "text-[#A9DBA9]" : codeState === "error" ? "text-[#E1574F]" : "text-[#161616]")}>
									{digit}
								</span> : <span key={i} className="grid w-3 place-items-center">
									<span className="size-2 rounded-full bg-[#D9D9D9]" />
								</span>;
          })}
					</label>
					{left > 0 ? <p className="text-[14px] leading-[18px] text-black/50">
							Получить новый код можно через 00:{String(left).padStart(2, "0")}
						</p> : <button type="button" disabled={busy} onClick={() => void start()} className="rounded-sm text-[14px] leading-[18px] text-[#1F1F1F] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
							Получить новый код
						</button>}
				</div>}
		</div>
  );
}